import React, { useState, useEffect } from 'react';
import { useTranslation } from 'next-i18next';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import clsx from 'clsx';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Gallery from './Gallery';
import Upload from '../molecules/Upload';
import Download from '../molecules/Download';
import Offset from './Offset';
import Property from '../atoms/Property';
import Loading from '../atoms/Loading';
import Button from '../atoms/Button';
import Input from '../atoms/Input';
import {
  createVenueSpaceState,
  updateVenueSpaceState,
  deleteVenueSpaceState,
} from '../../features/venue/venueSlice';
import {
  createVenueSpace,
  updateVenueSpace,
  deleteVenueSpace,
} from '../../lib/apis/venueSpace';
import { deleteVenueFile } from '../../features/venue/venueSlice';
import { deleteFile, uploadFile } from '../../lib/apis/file';
import { clearObj, getFormData, toClass, getErrorMsg } from '../../lib/utils';
import { upload_image, delete_image } from '../../lib/apis/image';
import styles from '../../styles/components/organisms/VenueSpaceLayer.module.scss';
import formStyles from '../../styles/components/organisms/VenueSpaceForm.module.scss';

interface VenueSpaceLayerProps {
  venueID: string;
  data: any;
  access?: boolean;
  apply: (id: string) => void;
  setLayer: (e: any) => void;
  setFullLayer: (full: boolean) => void;
}

export default function VenueSpaceLayer(props: VenueSpaceLayerProps) {
  const { t } = useTranslation(['common', 'venue']);
  const dispatch = useAppDispatch();
  const [edit, setEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState() as any;
  const [fieldErrors, setFieldErrors] = useState([]) as any;
  const [gallery, setGallery] = useState([]) as any;
  const [files, setFiles] = useState([]) as any;
  const [removedFiles, setRemovedFiles] = useState([]) as any;
  const [removedImages, setRemovedImages] = useState([]) as any;
  const [confirm, setConfirm] = useState(false);
  const { venueID, data, access, apply, setLayer, setFullLayer } = props;
  const { id, type, name, images, specs } = data || {};
  const typeLabel = t(`venue:spaces:${toClass(type)}`);
  const formID = 'spaceForm';
  const confirmMode = !!(id && confirm);

  const formClasses = clsx(
    formStyles.venueSpaceForm,
    { [formStyles.loading]: loading },
    { [formStyles.error]: !!error || !!fieldErrors.length }
  );
  const classes = clsx(
    styles.venueSpaceLayer,
    { [styles.access]: access },
    formClasses
  );

  const exclude = edit
    ? ['l_w_h', 'gates_w_h', 'images', 'capacity_per_type']
    : [
        'length',
        'width',
        'height',
        'gates_width',
        'gates_height',
        'pax_type_1', // frontal
        'pax_type_2', // frontal table
        'pax_type_3', // long table
        'pax_type_4', // horseshoe
        'pax_type_5', // groups
        'pax_type_6', // party
      ];
  const properties = specs.filter((el: any) => exclude.indexOf(el.id) === -1);
  /**
   * Handle errors
   */
  const handleError = (error: any) => {
    const errorMsg = getErrorMsg(error);
    setError(errorMsg);
    setLoading(false);
  };

  /**
   * Handle contact input
   */

  // Add VenueSpace
  const createSpace = (obj: any) => {
    try {
      // Save new VenueSpace to database and update venueState
      createVenueSpace(obj)
        .then((response) => {
          console.log('createVenueSpace', obj, response);
          dispatch(createVenueSpaceState(response));
          updateGallery(response);
        })
        .catch((error: any) => {
          handleError(error);
        });
    } catch (error) {
      handleError(error);
    }
  };

  // Update VenueSpace
  const updateSpace = (id: string, obj: any) => {
    try {
      // Save updated VenueSpace to database and update venueState
      updateVenueSpace(id, obj)
        .then((response) => {
          console.log('updateVenueSpace', obj, response);
          updateGallery(response);
        })
        .catch((error: any) => {
          handleError(error);
        });
    } catch (error) {
      handleError(error);
    }
  };

  // Delete VenueSpace
  const deleteSpace = (id: string) => {
    try {
      // Delete VenueSpace to database and update venueState
      deleteVenueSpace(id)
        .then((response) => {
          console.log('deleteVenueSpace', response);
          setLoading(false);
          dispatch(deleteVenueSpaceState(response));
          onApply();
        })
        .catch((error: any) => {
          handleError(error);
        });
    } catch (error) {
      handleError(error);
    }
  };

  /**
   * Handle images
   */

  const onImageRemove = (file: any) => {
    const { image_id } = file;
    if (!image_id) {
      return;
    }
    setRemovedImages([
      ...removedImages.filter((str: string) => str !== image_id),
      image_id,
    ]);
  };

  const updateGallery = async (payload: any) => {
    for (const image_id of removedImages) {
      await delete_image(image_id)
        .then((response: any) => {
          console.log('deletedFile', image_id);
        })
        .catch((error: any) => {
          handleError(error);
        });
    }

    const curGallery = (payload.images || []).filter(
      (el: any) => removedImages.indexOf(el.image_id) === -1
    );

    if (!!gallery.length) {
      const uploaded = [] as any;
      console.log('upload images', gallery);
      for (const file of gallery) {
        upload_image('venue-space', 'gallery', payload.id, file as any)
          .then((response: any) => {
            // logoField.value = '';
            uploaded.push(response);
            console.log('uploaded image', response);
            if (uploaded.length === gallery.length) {
              // field.value = '';
              updateFiles({
                ...payload,
                images: [...curGallery, ...uploaded],
              });
            }
          })
          .catch((error) => {
            handleError(error);
            return false;
          });
      }
    } else {
      updateFiles({
        ...payload,
        images: curGallery,
      });
    }
  };

  /**
   * Handle Files
   */
  const onFileRemove = (file: any) => {
    const { id } = file;
    if (!id) {
      return;
    }
    // console.log('onFileRemove', id);
    setRemovedFiles([...removedFiles.filter((str: string) => str !== id), id]);
  };

  const updateFiles = async (payload: any) => {
    for (const id of removedFiles) {
      await deleteFile(id)
        .then((response: any) => {
          // console.log('deletedFile', id);
        })
        .catch((error: any) => {
          handleError(error);
        });
    }

    if (!!files?.length) {
      const uploaded = [] as any;
      // console.log('upload files', newFiles);
      for (const file of files) {
        const obj = clearObj({
          file,
          ...file,
          category: `floorplan-${payload.id}`,
          venue_id: venueID,
          // caption: fields.description,
          // type: fields.type,
          // image: '',
          // url: fields.website,
        });
        // console.log('uploadFile', obj);
        // setLoading(false);
        // return;
        uploadFile(obj)
          .then((response: any) => {
            uploaded.push(response);
            console.log('uploaded file', response);
            if (uploaded.length === files.length) {
              completeSubmit({ ...payload, floorplan: response });
            }
          })
          .catch((error) => {
            handleError(error);
            return false;
          });
      }
    } else {
      completeSubmit(payload);
    }
  };

  const completeSubmit = (result: any) => {
    console.log('completeUpdate', result);
    // if (!!files.length) {
    //   dispatch(createVenueFile(result.floorplan));
    // } else
    if (!!removedFiles.length) {
      dispatch(deleteVenueFile(removedFiles[0]));
    }
    dispatch(updateVenueSpaceState(result));
    setRemovedImages([]);
    setRemovedFiles([]);
    setLoading(false);
    onApply();
  };

  /**
   * Handle form submit
   */
  const submit = (deleteID?: string) => {
    if (error) {
      return;
    }

    if (!!deleteID) {
      setLoading(true);
      deleteSpace(deleteID);
      return;
    }

    const formData = getFormData(formID);
    if (!formData.name) {
      setError(t(`common:form-input-error`));
      setFieldErrors(['name']);
      return;
    }
    const mergeFields = [
      'pax_type_1', // frontal
      'pax_type_2', // frontal table
      'pax_type_3', // long table
      'pax_type_4', // horseshoe
      'pax_type_5', // groups
      'pax_type_6',
    ];

    const capacity_per_type =
      type === 'CONFERENCE'
        ? mergeFields.map((key) => formData[key]).join(' / ')
        : null;
    const payload = { ...formData, capacity_per_type };
    delete payload.pax_type_1;
    delete payload.pax_type_2;
    delete payload.pax_type_3;
    delete payload.pax_type_4;
    delete payload.pax_type_5;
    delete payload.pax_type_6;
    delete payload.venueSpaceImages;
    delete payload.file;
    if (!capacity_per_type) {
      delete payload.capacity_per_type;
    }
    console.log('submit', id, payload, fieldErrors);
    setError(false);
    setFieldErrors([]);
    setLoading(true);
    if (id) {
      updateSpace(id, payload);
    } else {
      createSpace(payload);
    }
  };

  const onApply = () => {
    apply('spaceForm');
    setEdit(false);
  };

  useEffect(() => {
    if (access && !!!id) {
      setEdit(true);
    }
  }, [access, id]);

  // useEffect(() => {
  //   console.log('setGallery', images);
  //   setGallery(images || []);
  // }, [images, setGallery]);

  const editButtons = access ? (
    <>
      {confirm && (
        <p>
          <small className={formStyles.alert}>
            {t('common:confirm-delete', { typeLabel })}
          </small>
        </p>
      )}
      {error && (
        <p>
          <small className={formStyles.alert}>{error}</small>
        </p>
      )}
      {edit ? (
        <Button
          small
          dark
          icon="check"
          label={
            confirmMode ? t('common:cta-confirm-delete') : t('common:cta-apply')
          }
          onClick={() => {
            confirmMode ? submit(id) : submit();
          }}
        />
      ) : (
        <Button
          small
          dark
          icon={confirmMode ? 'check' : 'edit'}
          label={
            confirmMode ? t('common:cta-confirm-delete') : t('common:cta-edit')
          }
          onClick={() => {
            confirmMode ? submit(id) : setEdit(true);
          }}
        />
      )}

      {(edit || confirmMode) && (
        <Button
          small
          white
          label={t('common:cta-cancel')}
          icon="cancel"
          onClick={() => {
            confirmMode
              ? setConfirm(false)
              : id
              ? setEdit(false)
              : setLayer(null);
          }}
        />
      )}

      {access && !!id && !confirmMode && (
        <Button
          small
          red
          label={t('common:cta-delete')}
          icon="delete"
          onClick={() => {
            setConfirm(true);
          }}
        />
      )}
    </>
  ) : (
    ''
  );

  return (
    <Offset>
      <Container className={classes}>
        <form
          id={formID}
          onChange={() => {
            setError(false);
            setFieldErrors([]);
          }}
        >
          {access ? (
            <Row>
              <Col>
                <h2>{edit ? typeLabel : name}</h2>
                {editButtons}
              </Col>
            </Row>
          ) : (
            <Row>
              <Col>
                <h2>{name}</h2>
              </Col>
            </Row>
          )}

          {edit && (
            <Row>
              <Col>
                <Offset>
                  <input name="type" type="hidden" value={type} />
                  <input name="venue_id" type="hidden" value={venueID} />
                  <Input
                    name="name"
                    icon="halls"
                    half
                    label={t(`venue:spaces:name`, { type: typeLabel })}
                    error={fieldErrors.indexOf('name') > -1}
                    defaultValue={name || undefined}
                  />
                  <Upload
                    optional
                    multiple
                    half
                    type="image"
                    name={'venueSpaceImages'}
                    data={images}
                    onChange={(gallery) => {
                      setGallery(gallery);
                    }}
                    onError={(error) => {
                      setError(error);
                    }}
                    onRemove={onImageRemove}
                  />

                  {properties.map((el: any, i: number) =>
                    el.id === 'floorplan' ? (
                      <Upload
                        key={i}
                        half
                        float
                        optional
                        formats={['pdf']}
                        name={'file'}
                        data={!!el.value?.url ? el.value : ''}
                        label={t(`venue:spaces:${el.id}`)}
                        onChange={(files) => {
                          setFiles(files);
                        }}
                        onError={(error) => {
                          setError(error);
                        }}
                        onRemove={onFileRemove}
                      />
                    ) : (
                      <Input
                        key={i}
                        optional
                        float
                        medium
                        reset
                        // third={el.id !== 'description'}
                        full={el.id === 'description'}
                        name={el.id}
                        icon={el.id}
                        label={el.label}
                        defaultValue={el.value}
                        type={el.type}
                        format={el.format}
                        unit={el.unit}
                        convertTo={el.convertTo}
                        placeholder={el.placeholder}
                      />
                    )
                  )}

                  <br />
                </Offset>
              </Col>
            </Row>
          )}

          {/* Gallery */}
          {!edit && !!images && !!images.length && (
            <Row>
              <Col xs={8}>
                <br />
                <Gallery
                  thumbs
                  data={images.filter((img: any) => access || !img.hidden)}
                  toggle={(full) => {
                    setFullLayer(full);
                  }}
                />
                <br />
              </Col>
            </Row>
          )}

          {!edit && (
            <Row>
              <Col xs={8}>
                <Offset>
                  {properties.map((el: any, i: number) =>
                    el.id === 'floorplan' && !!el.value?.url ? (
                      <Download
                        key={i}
                        name={t(`venue:floorplan`)}
                        description={el.value.name}
                        type={el.value.type}
                        icon={el.id}
                        href={el.value.href}
                        url={el.value.url}
                      />
                    ) : (
                      <Property
                        key={i}
                        float
                        medium
                        full={properties.length < 4}
                        icon={el.id}
                        label={el.label}
                        value={el.format === 'file' ? '' : el.value}
                        unit={el.unit}
                      />
                    )
                  )}
                </Offset>
              </Col>
            </Row>
          )}
          {access && (
            <Row>
              <Col>{editButtons}</Col>
            </Row>
          )}

          <Row>
            <Col>
              <br /> <br />
              <br />
            </Col>
          </Row>
        </form>
      </Container>
      {loading && <Loading overlay />}
    </Offset>
  );
}
